import axios from 'axios';
import { isCapacitor } from '../utils/platform';

// API base URL from environment or fallback to localhost for development
const DEFAULT_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

class ApiService {
  constructor() {
    // Initialize with default URL, will be updated by loadConfiguration()
    this.client = axios.create({
      baseURL: DEFAULT_API_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    this.configurationLoaded = false;
    this.configuration = {};

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response.data,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - clear auth state
          this.setAuthToken(null);
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token) {
    this.authToken = token;
  }

  getAuthToken() {
    return this.authToken || localStorage.getItem('authToken');
  }

  // Load runtime configuration from server
  async loadConfiguration() {
    if (this.configurationLoaded) {
      return; // Already loaded
    }

    // For Capacitor (mobile apps), use baked-in VITE_API_URL from build time
    if (isCapacitor() && import.meta.env.VITE_API_URL) {
      this.configurationLoaded = true;
      return;
    }

    // For web, always use dynamic configuration from /config endpoint
    // This allows the same build to work in different environments (QA, production)

    try {
      // Fetch configuration from the current origin
      const response = await fetch('/config');
      if (!response.ok) {
        throw new Error(`Configuration request failed: ${response.status}`);
      }
      
      const config = await response.json();
      
      // Store the full configuration
      this.configuration = config;
      
      if (config.apiUrl) {
        // Update the axios client's base URL
        this.client.defaults.baseURL = config.apiUrl;
      }
      
      this.configurationLoaded = true;
    } catch (error) {
      console.warn('Failed to load configuration, using default API URL:', DEFAULT_API_URL, error);
      // Keep using the default URL
      this.configurationLoaded = true; // Mark as loaded to avoid retrying
    }
  }

  // Get configuration values
  getConfiguration() {
    return this.configuration;
  }

  getGoogleMapsApiKey() {
    return this.configuration.googleMapsApiKey || null;
  }

  // Geocoding endpoint
  async geocodeCoordinates(latitude, longitude) {
    return this.client.post('/api/geocode', { latitude, longitude });
  }

  // Weather endpoint
  async getWeather(locations) {
    return this.client.post('/api/weather', { locations });
  }

  // Places API endpoints
  async searchPlaces(query, sessionToken = null) {
    const payload = { query };
    if (sessionToken) {
      payload.session_token = sessionToken;
    }
    return this.client.post('/api/places/search', payload);
  }

  async getPlaceDetails(placeId, sessionToken = null) {
    const payload = { place_id: placeId };
    if (sessionToken) {
      payload.session_token = sessionToken;
    }
    return this.client.post('/api/places/details', payload);
  }

  // Registration status endpoint
  async getRegistrationStatus() {
    return this.client.get('/registration-status');
  }

  // Authentication endpoints
  async register(email, password) {
    return this.client.post('/register', { email, password });
  }

  async registerWithLink(token, email, password) {
    return this.client.post('/register-with-link', { token, email, password });
  }

  async login(email, password) {
    return this.client.post('/login', { email, password });
  }

  async getCurrentUser() {
    return this.client.get('/api/me');
  }

  // Health check
  async getStatus() {
    return this.client.get('/status');
  }

  // Todo Lists endpoints
  async getLists() {
    return this.client.get('/api/todo-lists');
  }

  async createList(title, tasks = [], locationData = null, address = null) {
    const payload = { title, tasks };
    if (locationData) {
      payload.location_data = locationData;
    }
    if (address) {
      payload.address = address;
    }
    return this.client.post('/api/todo-lists', payload);
  }

  async getList(id) {
    return this.client.get(`/api/todo-lists/${id}`);
  }

  async updateList(id, updates) {
    return this.client.put(`/api/todo-lists/${id}`, updates);
  }

  async deleteList(id) {
    return this.client.delete(`/api/todo-lists/${id}`);
  }

  async reorderLists(listIds) {
    return this.client.put('/api/todo-lists/reorder', { listIds });
  }

  // Todo Tasks endpoints
  async getTasks(listId) {
    return this.client.get(`/api/todo-lists/${listId}/tasks`);
  }

  async createTask(listId, title, locationData = null) {
    const payload = { title };
    if (locationData) {
      payload.location_data = locationData;
    }
    return this.client.post(`/api/todo-lists/${listId}/tasks`, payload);
  }

  async updateTask(listId, taskId, updates) {
    return this.client.put(`/api/tasks/${taskId}`, updates);
  }

  async deleteTask(listId, taskId) {
    return this.client.delete(`/api/tasks/${taskId}`);
  }

  async reorderTasks(listId, taskIds) {
    return this.client.put(`/api/todo-lists/${listId}/tasks/reorder`, { taskIds });
  }

  // Bulk task operations
  async completeAllTasks(listId) {
    return this.client.put(`/api/todo-lists/${listId}/complete-all`);
  }

  async incompleteAllTasks(listId) {
    return this.client.put(`/api/todo-lists/${listId}/incomplete-all`);
  }

  // List sharing endpoints
  async shareListByEmail(listId, email) {
    return this.client.post(`/api/todo-lists/${listId}/share`, { email });
  }

  // Export list as JSON file download
  async exportListAsJSON(listId) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    // Use fetch for file download since axios doesn't handle blob downloads well
    const response = await fetch(`${this.client.defaults.baseURL}/api/todo-lists/${listId}/export`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.setAuthToken(null);
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return;
      }
      throw new Error(`Export failed: ${response.status}`);
    }

    // Get filename from response headers
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'todo-list.json';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  // Admin endpoints
  async getUsers(page = 1, limit = 50) {
    return this.client.get(`/api/admin/users?page=${page}&limit=${limit}`);
  }

  async getUser(userId) {
    return this.client.get(`/api/admin/users/${userId}`);
  }

  async updateUser(userId, updates) {
    return this.client.put(`/api/admin/users/${userId}`, updates);
  }

  async deleteUser(userId) {
    return this.client.delete(`/api/admin/users/${userId}`);
  }

  // Admin registration link management endpoints
  async createRegistrationLink() {
    return this.client.post('/api/admin/registration-links');
  }

  async getRegistrationLinks() {
    return this.client.get('/api/admin/registration-links');
  }

  async deleteRegistrationLink(linkId) {
    return this.client.delete(`/api/admin/registration-links/${linkId}`);
  }

  // Password reset endpoints
  async forgotPassword(email) {
    return this.client.post('/forgot-password', { email });
  }

  async resetPassword(token, password) {
    return this.client.post('/reset-password', { token, password });
  }

  // Static Map endpoint - returns full response with headers for blob handling
  async getStaticMap(staticMapRequest, config = {}) {
    // Make request without response interceptor for this specific call
    const response = await axios.post('/api/static-map', staticMapRequest, {
      ...config,
      baseURL: this.client.defaults.baseURL,
      timeout: this.client.defaults.timeout,
      headers: {
        ...this.client.defaults.headers,
        ...config.headers,
        Authorization: this.getAuthToken() ? `Bearer ${this.getAuthToken()}` : undefined
      }
    });
    return response;
  }
}

const apiServiceInstance = new ApiService();

export const apiService = apiServiceInstance;
export default apiServiceInstance;
